import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Login } from './pages/Login.tsx';
import { EmployeeDashboard } from './pages/EmployeeDashboard.tsx';
import { MyIdeas } from './pages/MyIdeas.tsx';
import { NewIdea } from './pages/NewIdea.tsx';
import { IdeaDetails } from './pages/IdeaDetails.tsx';
import { AdminDashboard } from './pages/AdminDashboard.tsx';
import { CommitteeHeadDashboard } from './pages/CommitteeHeadDashboard.tsx';
import { CommitteeMemberDashboard } from './pages/CommitteeMemberDashboard.tsx';
import { CommitteeLeaderDashboard } from './pages/CommitteeLeaderDashboard.tsx';
import { ExternalCommitteeDashboard } from './pages/ExternalCommitteeDashboard.tsx';
import { ExecutionCommitteeDashboard } from './pages/ExecutionCommitteeDashboard.tsx';
import { SetPin } from './pages/SetPin.tsx';
import { AdminLogin } from './pages/AdminLogin.tsx';
import { UserMaster } from './pages/UserMaster.tsx';
import { Reports } from './pages/Reports.tsx';
import { AdvancedReports } from './pages/AdvancedReports.tsx';
import { AdminDeletedLog } from './pages/AdminDeletedLog.tsx';
import { AdminDepartmentMapping } from './pages/AdminDepartmentMapping.tsx';
import { DepartmentMappings } from './pages/DepartmentMappings.tsx';
import { DepartmentMappingsV2 } from './pages/DepartmentMappingsV2.tsx';
import { Profile } from './pages/Profile.tsx';
import { Help } from './pages/Help.tsx';
import { MyTeamPage } from './pages/MyTeamPage.tsx';
import { ImportBulkAssigneePage } from './pages/ImportBulkAssigneePage.tsx';
import { BulkEvaluateImportPage } from './pages/BulkEvaluateImportPage.tsx';
import { BulkStatusImportPage } from './pages/BulkStatusImportPage.tsx';
import { Dom680ReportPage } from './pages/Dom680ReportPage.tsx';
import { Dom680TargetSettingsPage } from './pages/Dom680TargetSettingsPage.tsx';
import AdminAuditPage from './pages/AdminAuditPage.tsx';
import { BulkReportPage } from './pages/BulkReportPage.tsx';
import { MatrixPage } from './pages/MatrixPage.tsx';
import { MisPortalPage } from './pages/MisPortalPage.tsx';
import { MisIdeaPage } from './pages/MisIdeaPage.tsx';
import 'react-datepicker/dist/react-datepicker.css';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children, adminOnly = false, moderatorAllowed = false, headAllowed = false, employeeOnly = false, evaluatorOnly = false, misOnly = false, misEmployee = false, requireProfile = true }: { children: React.ReactNode; adminOnly?: boolean; moderatorAllowed?: boolean; headAllowed?: boolean; employeeOnly?: boolean; evaluatorOnly?: boolean; misOnly?: boolean; misEmployee?: boolean; requireProfile?: boolean }) {
  const token = localStorage.getItem('accessToken');
  const role = localStorage.getItem('userRole');
  const subCoreRole = localStorage.getItem('userSubCoreRole') || '';
  const isProfileComplete = localStorage.getItem('isProfileComplete') === 'true';

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (role === 'MIS_COMMITTEE' && !misOnly && !misEmployee) {
    return <Navigate to="/mis" replace />;
  }

  if (misOnly && role !== 'MIS_COMMITTEE') {
    if (role === 'ADMIN' || role === 'APPROVAL_TEAM') return <Navigate to="/admin" replace />;
    if (role === 'COMMITTEE_HEAD') return <Navigate to="/committee-head" replace />;
    if (role === 'COMMITTEE_MEMBER') return <Navigate to="/committee-member" replace />;
    if (role === 'LEADER') return <Navigate to="/leader" replace />;
    if (role === 'EXTERNAL_COMMITTEE') return <Navigate to="/external-committee" replace />;
    if (role === 'EXECUTION_COMMITTEE') return <Navigate to="/execution-committee" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  const isModerator = role === 'LEADER' || role === 'COMMITTEE_HEAD' || role === 'COMMITTEE_MEMBER' || role === 'EXTERNAL_COMMITTEE' || role === 'EXECUTION_COMMITTEE';
  const isAdmin = role === 'ADMIN' || role === 'APPROVAL_TEAM';
  const isExternalOrExecution = role === 'EXTERNAL_COMMITTEE' || role === 'EXECUTION_COMMITTEE';

  if (evaluatorOnly && (role === 'LEADER' || subCoreRole === 'EXECUTOR' || isExternalOrExecution)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly) {
    if (!isAdmin && !(moderatorAllowed && isModerator) && !(headAllowed && role === 'COMMITTEE_HEAD')) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (employeeOnly && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (role !== 'ADMIN' && role !== 'APPROVAL_TEAM' && requireProfile && !isProfileComplete) {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
}

function RootRedirect() {
  const role = localStorage.getItem('userRole');
  if (role === 'ADMIN' || role === 'APPROVAL_TEAM') return <Navigate to="/admin" replace />;
  if (role === 'COMMITTEE_HEAD') return <Navigate to="/committee-head" replace />;
  if (role === 'COMMITTEE_MEMBER') return <Navigate to="/committee-member" replace />;
  if (role === 'LEADER') return <Navigate to="/leader" replace />;
  if (role === 'EXTERNAL_COMMITTEE') return <Navigate to="/external-committee" replace />;
  if (role === 'EXECUTION_COMMITTEE') return <Navigate to="/execution-committee" replace />;
  if (role === 'MIS_COMMITTEE') return <Navigate to="/mis" replace />;
  return <Navigate to="/dashboard" replace />;
}

/** Same role dashboard as home, with Future potential by EC card enabled */
function FullReportDashboard() {
  const role = localStorage.getItem('userRole');
  if (role === 'ADMIN' || role === 'APPROVAL_TEAM') {
    return <AdminDashboard showFuturePotentialByEc />;
  }
  if (role === 'LEADER') {
    return <CommitteeLeaderDashboard showFuturePotentialByEc />;
  }
  if (role === 'COMMITTEE_HEAD') {
    return <CommitteeHeadDashboard showFuturePotentialByEc />;
  }
  if (role === 'COMMITTEE_MEMBER') {
    return <CommitteeMemberDashboard showFuturePotentialByEc />;
  }
  return <RootRedirect />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-pin" element={<SetPin />} />
          <Route path="/" element={<NewIdea />} />
          <Route path="/ideas/new" element={<NewIdea />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute employeeOnly={true} misEmployee={true}>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ideas"
            element={
              <ProtectedRoute employeeOnly={true} misEmployee={true}>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideas/edit/:id"
            element={
              <ProtectedRoute employeeOnly={true} misEmployee={true}>
                <NewIdea isEdit={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideas/:id"
            element={
              <ProtectedRoute misEmployee={true}>
                <IdeaDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideas/:id/tdx007"
            element={
              <ProtectedRoute adminOnly={true}>
                <IdeaDetails isTdx={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute requireProfile={false} misEmployee={true}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={<Help />}
          />
          <Route
            path="/mis"
            element={
              <ProtectedRoute misOnly={true} requireProfile={false}>
                <MisPortalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mis/ideas/:id"
            element={
              <ProtectedRoute misOnly={true} requireProfile={false}>
                <MisIdeaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-team"
            element={
              <ProtectedRoute>
                <MyTeamPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Moderation Portal */}
          <Route path="/admin/login" element={<AdminLogin />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/committee-head"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <CommitteeHeadDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/committee-member"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <CommitteeMemberDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leader"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <CommitteeLeaderDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/full-report"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <FullReportDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/external-committee"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <ExternalCommitteeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/execution-committee"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <ExecutionCommitteeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/deleted-ideas"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDeletedLog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly={true}>
                <UserMaster />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/department-mapping"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDepartmentMapping />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/department-mapping-v2"
            element={
              <ProtectedRoute adminOnly={true}>
                <DepartmentMappingsV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/department-grouping"
            element={
              <ProtectedRoute adminOnly={true}>
                <DepartmentMappings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/import-bulk-assignee"
            element={
              <ProtectedRoute adminOnly={true} headAllowed={true}>
                <ImportBulkAssigneePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-evaluate-import"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true} evaluatorOnly={true}>
                <BulkEvaluateImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-status-import"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true} evaluatorOnly={true}>
                <BulkStatusImportPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAuditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/reports/metrix"
            element={
              <ProtectedRoute adminOnly={true}>
                <MatrixPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/metrix" element={<Navigate to="/admin/reports/metrix" replace />} />
          <Route path="/admin/matrix" element={<Navigate to="/admin/reports/metrix" replace />} />

          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute adminOnly={true}>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/advanced"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdvancedReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/dom680"
            element={
              <ProtectedRoute adminOnly={true} moderatorAllowed={true}>
                <Dom680ReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/dom680/targets"
            element={
              <ProtectedRoute adminOnly={true}>
                <Dom680TargetSettingsPage />
              </ProtectedRoute>
            }
          />


          <Route
            path="/bulkreport"
            element={
              <ProtectedRoute adminOnly={true}>
                <BulkReportPage />
              </ProtectedRoute>
            }
          />

          {/* Default Route redirection */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
